import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import './NextStep.css'

/** Icon per secondary-action key, so a server reorder can't shuffle them. */
const ACTION_ICONS: Record<string, React.ReactNode> = {
  trust: <><path d="m2.8 5.6 1.3 1.3 2.2-2.4M2.8 10.2l1.3 1.3 2.2-2.4" /><circle cx="4.3" cy="15" r="1.7" /><path d="M9.4 5.4h7.8M9.4 10h7.8M9.4 14.9h5.2" /></>,
  invite: <><circle cx="8" cy="6.8" r="2.8" /><path d="M2.8 16.5c0-2.9 2.3-4.8 5.2-4.8s5.2 1.9 5.2 4.8M16 6v5M13.5 8.5h5" /></>,
  docs: <><path d="M5.5 2.8h6L15 6.3v11h-9.5z" /><path d="M11.2 2.9v3.6h3.6M8 11h4M8 13.5h4" /></>,
  shared: <><circle cx="7.4" cy="7" r="2.6" /><circle cx="13.4" cy="8" r="2.2" /><path d="M2.8 16c0-2.6 2-4.5 4.6-4.5s4.6 1.9 4.6 4.5M13 11.8c2.1 0 3.8 1.7 3.8 3.8" /></>,
}

export function NextStep() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['next-step'],
    queryFn: () => api.getNextStep(),
  })

  return (
    <section className="sec">
      <div className="hd">
        <div>
          <h2 className="h2">Recommended Next Step</h2>
          <p className="sub">See the most important action to move your executive hiring process forward</p>
        </div>
      </div>

      {isError && (
        <div className="card" style={{ padding: '26px 20px', textAlign: 'center' }} role="alert">
          <b style={{ display: 'block', marginBottom: 4 }}>Could not load your next step</b>
          <button className="btn" type="button" onClick={() => void refetch()} style={{ marginTop: 10 }}>
            Try again
          </button>
        </div>
      )}

      {!isError && (
      <div className="next">
        <div className="hero" aria-busy={isPending}>
          <div className="hero-c" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
              <path d="m12 2.6 2.9 5.9 6.5 1-4.7 4.5 1.1 6.4L12 17.4l-5.8 3 1.1-6.4L2.6 9.5l6.5-1z" />
            </svg>
          </div>
          <div>
            <h3 className="hero-h">{data?.headline ?? ' '}</h3>
            <p className="hero-p">{data?.detail ?? ''}</p>
            {data && (
              <Link className="btn btn-teal" to={data.cta_url} style={{ marginTop: 14 }}>
                {data.cta_label} &nbsp;→
              </Link>
            )}
          </div>
        </div>

        <div className="n4">
          {isPending && [0, 1, 2, 3].map((i) => (
            <div className="card n4c rc-skeleton" key={i} style={{ minHeight: 74 }} />
          ))}
          {/* the contract sends a url per action — these were rendered as inert
              divs, so the server's routing intent was fetched and discarded */}
          {data?.secondary.map((action) => (
            <Link className="card n4c" to={action.url} key={action.key}>
              <div className="n4i" aria-hidden="true">
                <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                  {ACTION_ICONS[action.key]}
                </svg>
              </div>
              <div>
                <div className="n4t">{action.title}</div>
                <div className="n4m">{action.detail}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      )}
    </section>
  )
}

const STEPS = [
  { n: 1, title: 'Add Role Details', detail: 'Tell us about the role and requirements' },
  { n: 2, title: 'Review AI Criteria', detail: 'See AI-generated hiring criteria and signals' },
  { n: 3, title: 'Search & Track', detail: 'Find matches and track progress in one place' },
]

function Arrow() {
  return (
    <svg className="arw" width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 10h11M11 6l4 4-4 4" />
    </svg>
  )
}

export function CreateRolePipeline() {
  return (
    <section className="card sec" style={{ padding: '18px 20px' }}>
      <h2 className="h2">Create a Role Pipeline</h2>
      <p className="sub">A guided 3-step workflow to define, search, and track executive talent</p>
      <div className="flow">
        {STEPS.map((step) => (
          <div key={step.n} style={{ display: 'contents' }}>
            <div className="stp">
              <div className="stp-n" aria-hidden="true">{step.n}</div>
              <div>
                <div className="stp-t">{step.title}</div>
                <div className="stp-m">{step.detail}</div>
              </div>
            </div>
            <Arrow />
          </div>
        ))}
        <div className="flow-a">
          <Link className="btn btn-teal" to="/pipelines">Create Role Pipeline</Link>
          <Link className="btn" to="/search">Upload Job Description</Link>
        </div>
      </div>
    </section>
  )
}
